import { AxiosRequestConfig } from "axios";
import { isMigrated, notifyBlocked } from "./migrationStore";
import { isAllowlisted } from "./allowlist";
import {
  EMPLOYER_READ_ONLY_CODE,
  BACKEND_READ_ONLY_CODE,
  READ_ONLY_TOAST_COPY,
} from "./constants";

export type ReadOnlyError = Error & {
  code: string;
  isReadOnly: true;
  isAxiosError: boolean;
  response: {
    status: number;
    data: { code: string; message: string; detail: string };
  };
};

type ReadOnlyResponseLike = {
  response?: { status?: number; data?: { code?: string } };
};

// Shaped like an AxiosError (with a response body) so feature onError handlers
// that read error.response.data.message/detail render coherent copy and never
// crash on a missing response. Status 423 (Locked) — deliberately NOT 403, so
// it can't trip any status===403 session-expiry logic and isBackendReadOnlyResponse
// (which matches 403) won't double-fire the modal for this client-side error.
export const makeReadOnlyError = (): ReadOnlyError => {
  const err = new Error(READ_ONLY_TOAST_COPY) as ReadOnlyError;
  err.code = EMPLOYER_READ_ONLY_CODE;
  err.isReadOnly = true;
  err.isAxiosError = true;
  err.response = {
    status: 423,
    data: {
      code: BACKEND_READ_ONLY_CODE,
      message: READ_ONLY_TOAST_COPY,
      detail: READ_ONLY_TOAST_COPY,
    },
  };
  return err;
};

export const isReadOnlyError = (err: unknown): boolean =>
  Boolean(
    err &&
      ((err as { isReadOnly?: boolean }).isReadOnly === true ||
        (err as { code?: string }).code === EMPLOYER_READ_ONLY_CODE)
  );

export const isBackendReadOnlyResponse = (error: unknown): boolean => {
  const e = error as ReadOnlyResponseLike;
  return (
    e?.response?.status === 403 &&
    e?.response?.data?.code === BACKEND_READ_ONLY_CODE
  );
};

// Request interceptor: block unsafe, non-allowlisted writes for migrated
// employers before they leave the browser. Returns the config untouched
// otherwise, so behaviour is byte-for-byte identical when not migrated.
export const readOnlyRequestInterceptor = (
  config: AxiosRequestConfig
): AxiosRequestConfig => {
  if (isMigrated() && !isAllowlisted(config.method, config.url)) {
    notifyBlocked();
    throw makeReadOnlyError();
  }
  return config;
};

// Response interceptor: surface the friendly modal for a backend read-only
// rejection, then always re-reject so existing onError paths still run.
export const readOnlyResponseErrorInterceptor = (
  error: unknown
): Promise<never> => {
  if (isBackendReadOnlyResponse(error)) {
    notifyBlocked();
  }
  return Promise.reject(error);
};
